declare var saveAs: any;

module EEX.McEc.Controls.CertificateDialog {

    import ReadPrivateKeyFileCommand = McEc.Commands.ReadPrivateKeyFileCommand;
    import GetKeyPairFromFileCommand = McEc.Commands.GetKeyPairFromFileCommand;

    enum UploadPrivateKeyStatus {
        None,
        EnteringPassword,
        Invalid,
        NoFileSelected,
        WrongPassword,
        PrivateKeyAndCertificateNotMatch,
        Ok
    }

    // ReSharper disable once InconsistentNaming
    export interface UploadPrivateKeyAttribute extends CertificateDialogBaseAttribute {
        privateKeyPassword: string,
        keyPairFile: File;
        status: UploadPrivateKeyStatus;
    }

    export class UploadPrivateKeyModel extends CertificateDialogBaseModel<UploadPrivateKeyAttribute> {

        private readPrivateKeyFileCommand: ReadPrivateKeyFileCommand;
        private getKeyPairFromFileCommand: GetKeyPairFromFileCommand;

        constructor(model: CertificateDialogModel, processStep: number, readPrivateKeyFileCommand: ReadPrivateKeyFileCommand,
            getKeyPairFromFileCommand: GetKeyPairFromFileCommand) {
            super(model, processStep,
                {
                    privateKeyPassword: "",
                    keyPairFile: null,
                    status: UploadPrivateKeyStatus.None
                });

            this.readPrivateKeyFileCommand = readPrivateKeyFileCommand;
            this.getKeyPairFromFileCommand = getKeyPairFromFileCommand;

            this.on("change:privateKeyPassword", this.updateDialogModel);
            this.on("change:keyPairFile", this.updateDialogModel);
        }

        public updateDialogModel(): void {
            const file = this.get().keyPairFile;
            const password = this.get().privateKeyPassword;
            if (file && password !== undefined) {
                this.readPrivateKeyFileCommand.read(file, (privateKeyFile) => this.successReadingFile(privateKeyFile), () => this.failReadingFile());
            }
        }

        private successReadingFile(privateKeyFile: string) {
            if (this.get().status === UploadPrivateKeyStatus.None) {
                this.set({ status: UploadPrivateKeyStatus.EnteringPassword });
            } else {
                const privateKeyPassword = this.get().privateKeyPassword;
                const certificateFile = this.model.get().certificateFile;
                this.getKeyPairFromFileCommand.execute(privateKeyFile, certificateFile, privateKeyPassword, (store) => this.getKeyPairFromFile(store),
                    () => this.invalidInput(),
                    () => this.wrongPassword(),
                    () => this.privateKeyAndCertificateNotMatch());
            }
        }

        private invalidInput(): void {
            this.set({ status: UploadPrivateKeyStatus.Invalid });
            this.canNotContinue();
        }

        private wrongPassword(): void {
            this.set({ status: UploadPrivateKeyStatus.WrongPassword });
            this.canNotContinue();
        }

        private privateKeyAndCertificateNotMatch(): void {
            this.set({ status: UploadPrivateKeyStatus.PrivateKeyAndCertificateNotMatch });
            this.canNotContinue();
        }

        private getKeyPairFromFile(store: Pki.Store): void {
            this.set({ status: UploadPrivateKeyStatus.Ok });
            this.model.set({ store: store });
            this.canContinue();
        }

        private failReadingFile(): void {
            this.canNotContinue();
            AppKitchen.UIHelper.Notifications.notify(McEc.Strings.CertificateDialog_UploadPrivateKey_Error_CouldNotCreateKeyPair,
                "warning",
                { closeAfter: 3000 });
        }
    }

    // ReSharper disable once InconsistentNaming
    export interface UploadPrivateKeyOptions extends CertificateDialogBaseOptions {
    }

    export class UploadPrivateKeyView extends CertificateDialogBaseView<UploadPrivateKeyModel> {
        model: UploadPrivateKeyModel;

        private isRemoved: boolean = false;

        private upload: kendo.ui.Upload;

        private containers: {
            form: HTMLElement,
        }

        private elements: {
            passwordInput: JQuery,
            passwordField: JQuery,
            upload: JQuery,
            uploadField: JQuery,
        }

        constructor(model: UploadPrivateKeyModel, target: HTMLElement, options?: UploadPrivateKeyOptions) {
            super(model,
                target,
                McEc.Templates.CertificateDialog_UploadPrivateKey,
                AppKitchen.OptionsHelper.merge(options,
                    {
                        title: McEc.Strings.CertificateDialog_UploadPrivateKey_Title
                    }));

            this.model.on("change:status", this.statusChanged.bind(this));
        }

        render() {
            super.render();
            this.setContainers();
            this.renderFields();
            this.setElements();
            this.setEvents();
            this.createUpload();

            return this;
        }

        private setContainers(): void {
            this.containers = {
                form: this.$el.find(".upload-private-key-form-container")[0]
            }
        }

        private renderFields(): void {
            const from = $('<div class="a-form">');
            const table = $('<table>');
            const passwordField = AppKitchen.UIHelper.renderTemplate(Templates.PasswordFormField,
                {
                    id: "password",
                    label: Strings.CertificateDialog_Password,
                    placeholder: Strings.CertificateDialog_Password,
                    tabindex: 2
                });

            $(passwordField).appendTo(table);
            table.appendTo(from);
            from.appendTo(this.containers.form);
        }

        private setElements(): void {
            this.elements = {
                passwordInput: this.$el.find("#password input"),
                passwordField: this.$el.find("#password"),
                upload: this.$el.find(".upload-private-key-input"),
                uploadField: this.$el.find(".upload-wrapper"),
            }
        }

        private setEvents(): void {
            this.elements.passwordInput.focusout(() => this.updateModel());
            this.elements.passwordInput.focus();
            this.enablePasswordField(false);
        }

        private createUpload(): void {
            this.upload = this.elements.upload.kendoUpload({
                async: {
                    saveUrl: "save",
                    removeUrl: "remove",
                    autoUpload: true
                },
                multiple: false,
                remove: (e) => this.onRemove(e),
                success: (e) => this.onSuccess(e),
                complete: (e) => this.onComplete(e),
            }).data("kendoUpload");
        }

        private onComplete(e): void {
            let statusLabel = e.sender.wrapper.find(".k-upload-status-total");
            let statusIcon = statusLabel.find(".k-i-check");
            statusLabel.empty();
            statusLabel.append(statusIcon);
            statusLabel.append(McEc.Strings.CertificateDialog_Finish);
        }

        private onRemove(e): void {
            this.clearMessage(this.elements.uploadField);
            this.clearMessage(this.elements.passwordField);
            this.enablePasswordField(false);
            this.clearPasswordField();
            this.isRemoved = true;
        }

        private onSuccess(e): void {
            this.clearPasswordField();
            this.model.canNotContinue();
            this.model.set({ status: UploadPrivateKeyStatus.None });
            this.updateModel();
            this.isRemoved = false;
        }

        private statusChanged(): void {
            let status = this.model.get().status;

            if (status === UploadPrivateKeyStatus.EnteringPassword) {
                this.enablePasswordField(true);
            }
            if (status === UploadPrivateKeyStatus.WrongPassword || status === UploadPrivateKeyStatus.Invalid) {
                this.enteredWrongPassword();
            } else if (status === UploadPrivateKeyStatus.PrivateKeyAndCertificateNotMatch) {
                this.privateKeyAndCertificateNotMatch();
            } else if (status === UploadPrivateKeyStatus.NoFileSelected) {
                this.noFileSelected();
            }
        }

        private enablePasswordField(enable: boolean): void {
            this.elements.passwordInput.prop("disabled", !enable);
        }

        private clearPasswordField(): void {
            this.clearValueFromElement(this.elements.passwordInput);
        }

        private enteredWrongPassword(): void {
            this.clearMessage(this.elements.uploadField);
            this.showMessage(this.elements.passwordField, McEc.Strings.CertificateDialog_UploadPrivateKey_Error_IncorrectPassword, "invalid");
        }

        private privateKeyAndCertificateNotMatch(): void {
            this.clearMessage(this.elements.passwordField);
            this.showMessage(this.elements.uploadField, McEc.Strings.CertificateDialog_UploadPrivateKey_Error_PrivateKeyAndCertificateNotMatch, "invalid");
        }

        private noFileSelected(): void {
            this.clearMessage(this.elements.passwordField);
            this.showMessage(this.elements.uploadField, McEc.Strings.CertificateDialog_UploadPrivateKey_Error_NoFileSelected, "invalid");
        }

        private clearValueFromElement(element: JQuery): void {
            element.val('');
        }

        private updateModel(): void {
            const password = this.getPasswordFromElement();
            const file = this.isRemoved ? null : this.getUploadedFile();
            this.model.set({
                privateKeyPassword: password,
                keyPairFile: file
            });
        }

        private getUploadedFile(): void {
            if (!this.upload) {
                return null;
            }
            const files = this.upload.getFiles();
            if (files.length !== 1) {
                return null;
            }
            return files[0].rawFile;
        }

        private getPasswordFromElement(): string {
            return this.getStringFromInput(this.elements.passwordInput);
        }

        private getStringFromInput(element: JQuery): string {
            const value = element.val();
            return value as string;
        }

        private clearMessage(element: JQuery): void {
            element.removeClass("mandatory");
            element.removeClass("warning");
            element.removeClass("invalid");
            element.find(".a-input-error-message .a-message").html("");
        }

        private showMessage(element: JQuery, message: string, messageClass: "mandatory" | "warning" | "invalid"): void {
            this.clearMessage(element);
            element.addClass(messageClass);
            element.find(".a-input-error-message .a-message").html(message);
        }
    }
}
